import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Box, InputBase, Typography, Paper, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import FolderIcon from "@mui/icons-material/Folder";
import { useNavigate } from "react-router-dom";
import { useApi } from "../utils/api";
import { ENDPOINTS } from "../config/endpoints";

const Topbar = ({ username, notificationCount = 3 }) => {
  const navigate = useNavigate();
  const http = useApi();

  const userData = useMemo(() => JSON.parse(localStorage.getItem("user")), []);
  const isAdmin = userData?.type !== "kurum";
  const isPersonel = userData?.type === "personel";

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({
    toplam_personel: null,
    toplam_kurum: null,
    eksik_dosya: null,
    toplam_dosya: null,
  });

  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isPersonel) return; // personel için stats çekme

    const fetchStats = async () => {
      try {
        if (userData?.type === "kurum") {
          const json = await http.get(ENDPOINTS.STATS.KURUM(userData.id));
          setStats({
            toplam_personel: json.toplam_personel,
            toplam_kurum: null,
            eksik_dosya: json.eksik_dosya_sayisi,
            toplam_dosya: json.toplam_dosya_sayisi,
          });
        } else {
          const json = await http.get(ENDPOINTS.STATS.ADMIN);
          setStats({
            toplam_personel: json.toplam_personel,
            toplam_kurum: json.toplam_kurum,
            eksik_dosya: json.eksik_dosya_sayisi,
            toplam_dosya: json.toplam_dosya_sayisi,
          });
        }
      } catch (err) {
        console.error("Stats yüklenemedi:", err);
      }
    };

    if (userData) fetchStats();
  }, []);

  const handleSearch = useCallback(
    (value) => {
      setQuery(value);

      if (!value.trim()) {
        setResults([]);
        setOpen(false);
        clearTimeout(debounceRef.current);
        return;
      }

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const params = { search: value, limit: 8, skip: 0 };
          if (!isAdmin) params.kurum_id = userData.id;

          const data = await http.get("/personeller", params);
          setResults(Array.isArray(data) ? data : []);
          setOpen(true);
        } catch (err) {
          console.error("Arama hatası:", err);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [http, isAdmin, userData]
  );

  const handleSelect = (personel) => {
    setQuery("");
    setResults([]);
    setOpen(false);
    navigate(`/personel/${personel.id}`);
  };

  return (
    <Box
      sx={{
        height: 60,
        bgcolor: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        borderBottom: "1px solid rgba(0,0,0,0.1)",
        borderLeft: "1px solid rgba(0,0,0,0.1)",
      }}
    >
      {/* Sol: Search - personel görmez */}
      {!isPersonel ? (
        <Box ref={wrapperRef} sx={{ position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "white",
              border: "1px solid #E5E7EB",
              px: 2,
              py: 0.5,
              borderRadius: 2,
            }}
          >
            {loading ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <SearchIcon sx={{ mr: 1, color: "gray" }} />
            )}
            <InputBase
              placeholder="Personel ara..."
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              sx={{ fontSize: 14, width: 220 }}
            />
          </Box>

          {/* Dropdown sonuçlar */}
          {open && results.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                top: "110%",
                left: 0,
                width: 300,
                zIndex: 9999,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {results.map((p) => (
                <Box
                  key={p.id}
                  onClick={() => handleSelect(p)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.2,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#F3F4F6" },
                    borderBottom: "1px solid #F3F4F6",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "#DBEAFE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#2563EB",
                      flexShrink: 0,
                    }}
                  >
                    {(p.ad || "?")[0].toUpperCase()}
                  </Box>

                  <Box>
                    <Typography fontSize={13} fontWeight={600}>
                      {p.ad} {p.soyad}
                    </Typography>
                    <Typography fontSize={11} color="text.secondary">
                      {p.tc} {p.departman?.ad ? `• ${p.departman.ad}` : ""}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          {/* Sonuç yok */}
          {open && !loading && query.trim() && results.length === 0 && (
            <Paper
              sx={{
                position: "absolute",
                top: "110%",
                left: 0,
                width: 280,
                zIndex: 9999,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
              }}
            >
              <Typography fontSize={13} color="text.secondary">
                Sonuç bulunamadı.
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
        <Box /> // personel için sol taraf boş
      )}

      {/* Sağ: Stats + User */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

        {/* İstatistik Chip'leri - personel görmez */}
        {!isPersonel && (
          <Box sx={{ display: "flex", gap: 1 }}>

            {/* Toplam Personel */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#EFF6FF",
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <PeopleIcon sx={{ fontSize: 16, color: "#2563EB" }} />
              <Typography fontSize={13} fontWeight={600} color="#2563EB">
                {stats.toplam_personel ?? "-"}
              </Typography>
              <Typography fontSize={11} color="#2563EB">
                Personel
              </Typography>
            </Box>

            {/* Toplam Kurum - sadece admin */}
            {isAdmin && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "#F0FDF4",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                <WorkIcon sx={{ fontSize: 16, color: "#16A34A" }} />
                <Typography fontSize={13} fontWeight={600} color="#16A34A">
                  {stats.toplam_kurum ?? "-"}
                </Typography>
                <Typography fontSize={11} color="#16A34A">
                  Kurum
                </Typography>
              </Box>
            )}

            {/* Toplam Dosya */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#F5F3FF",
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <FolderIcon sx={{ fontSize: 16, color: "#7C3AED" }} />
              <Typography fontSize={13} fontWeight={600} color="#7C3AED">
                {stats.toplam_dosya ?? "-"}
              </Typography>
              <Typography fontSize={11} color="#7C3AED">
                Dosya
              </Typography>
            </Box>

            {/* Eksik Dosya */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: stats.eksik_dosya > 0 ? "#FEF2F2" : "#F0FDF4",
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <FolderIcon
                sx={{
                  fontSize: 16,
                  color: stats.eksik_dosya > 0 ? "#DC2626" : "#16A34A",
                }}
              />
              <Typography
                fontSize={13}
                fontWeight={600}
                color={stats.eksik_dosya > 0 ? "#DC2626" : "#16A34A"}
              >
                {stats.eksik_dosya ?? "-"}
              </Typography>
              <Typography
                fontSize={11}
                color={stats.eksik_dosya > 0 ? "#DC2626" : "#16A34A"}
              >
                Eksik Dosya
              </Typography>
            </Box>
          </Box>
        )}

        {/* Kullanıcı - herkes görür */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon />
          <Typography fontWeight="bold" fontSize={14}>
            {username || "User Adı"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Topbar;